import React, { useCallback, useMemo } from 'react';
import {
  StudioContext,
  StudioSidePanelContext,
  StudioActionInterceptorProvider,
  useRunStudioActionInterceptors,
  type AfterUnsavedChangeAction,
  type StudioActionName,
} from 'venn-components';
import useSelectionSystem from '../logic/useSelectionSystem';
import useStudioSetup from '../logic/useStudioSetup';
import useStudioToolbar from '../logic/useStudioToolbar';
import useStudioExport from '../logic/useStudioExport';

const StudioContextStoreInner: React.FC<React.PropsWithChildren> = ({ children }) => {
  const runInterceptors = useRunStudioActionInterceptors();

  const setupProps = useStudioSetup();
  const {
    isGlobalAnalysisRangeLoading,
    firstOpeningOfTheView,
    setFirstOpeningOfTheView,
    afterUnsavedChangesAction,
    setAfterUnsavedChangesAction,
    setIsDuplicateReportName,
    setIsCheckingDuplicateReportName,
  } = setupProps;

  const wrappedSetAfterUnsavedChangesAction = useCallback(
    async (action?: AfterUnsavedChangeAction) => {
      if (!action) {
        setAfterUnsavedChangesAction(undefined);
        return;
      }

      const shouldProceed = await runInterceptors('setAfterUnsavedChangesAction');
      if (!shouldProceed) {
        return;
      }

      setAfterUnsavedChangesAction({
        ...action,
        proceedCallback: async (analysisViewName, analysisViewId) => {
          const shouldProceed = await runInterceptors('setAfterUnsavedChangesAction');
          if (shouldProceed) {
            action.proceedCallback(analysisViewName, analysisViewId);
          }
        },
      });
    },
    [runInterceptors, setAfterUnsavedChangesAction],
  );

  const sideBarProps = useSelectionSystem();
  const { onSelectBlock, onSelectGlobal, onSelectPage } = sideBarProps;

  const exportProps = useStudioExport();

  const toolBarProps = useStudioToolbar({
    firstOpeningOfTheView,
    setFirstOpeningOfTheView,
    afterUnsavedChangesAction,
    setAfterUnsavedChangesAction: wrappedSetAfterUnsavedChangesAction,
    onSelectPage,
    onSelectBlock,
    onSelectGlobal,
    onExport: exportProps.onExport,
    setIsDuplicateReportName,
    setIsCheckingDuplicateReportName,
  });

  const withInterceptors = useCallback(
    async (actionName: StudioActionName, fn: () => void | Promise<void>) => {
      const shouldProceed = await runInterceptors(actionName);
      if (shouldProceed) {
        await fn();
      }
    },
    [runInterceptors],
  );

  const wrappedOnExcelExport = useCallback(
    () => withInterceptors('onExcelExport', exportProps.onExcelExport),
    [withInterceptors, exportProps.onExcelExport],
  );

  const wrappedOnPdfExport = useCallback(
    (isInternal: boolean) => withInterceptors('onPdfExport', () => toolBarProps.onPdfExport(isInternal)),
    [withInterceptors, toolBarProps],
  );

  const wrappedOnSave = useCallback(async (): Promise<{ savedName?: string; savedId?: string }> => {
    const shouldProceed = await runInterceptors('onSave');
    if (!shouldProceed) {
      return {};
    }

    return (await toolBarProps.onSave?.()) ?? {};
  }, [runInterceptors, toolBarProps]);

  const wrappedOnSaveAs = useCallback(
    (name: string, ownerContextId?: string) =>
      withInterceptors('onSaveAs', () => toolBarProps.onSaveAs(name, ownerContextId)),
    [withInterceptors, toolBarProps],
  );

  const contextValue = useMemo(
    () => ({
      ...setupProps,
      setAfterUnsavedChangesAction: wrappedSetAfterUnsavedChangesAction,

      ...toolBarProps,
      onPdfExport: wrappedOnPdfExport,
      onSave: wrappedOnSave,
      onSaveAs: wrappedOnSaveAs,

      ...exportProps,
      onExcelExport: wrappedOnExcelExport,

      isGlobalAnalysisRangeLoading,
    }),
    [
      setupProps,
      wrappedSetAfterUnsavedChangesAction,
      toolBarProps,
      wrappedOnPdfExport,
      wrappedOnSave,
      wrappedOnSaveAs,
      exportProps,
      wrappedOnExcelExport,
      isGlobalAnalysisRangeLoading,
    ],
  );

  return (
    <StudioContext.Provider value={contextValue}>
      <StudioSidePanelContext.Provider value={sideBarProps}>{children}</StudioSidePanelContext.Provider>
    </StudioContext.Provider>
  );
};

const StudioContextStore: React.FC<React.PropsWithChildren<unknown>> = React.memo(function StudioContextStore({
  children,
}) {
  return (
    <StudioActionInterceptorProvider>
      <StudioContextStoreInner>{children}</StudioContextStoreInner>
    </StudioActionInterceptorProvider>
  );
});

export default StudioContextStore;
